import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Auth0ClientOptions, GetTokenSilentlyOptions } from '@auth0/auth0-spa-js';
import { OrganizationService } from './organization.service';
import { OrganizationData } from '../interfaces/auth0.interface';
import { isPublicRoute } from '../public-routes';

export interface Auth0User {
  email?: string;
  sub?: string;
  name?: string;
  picture?: string;
  [key: string]: unknown;
}

/**
 * Token response from Auth0 SDK when detailedResponse is true
 */
interface Auth0TokenResponse {
  access_token?: string;
  id_token?: string;
  token_type?: string;
  expires_in?: number;
}

@Injectable({
  providedIn: 'root'
})
export class Auth0Service {
  private router = inject(Router);
  private organizationService = inject(OrganizationService);

  private _isAuthenticated = signal<boolean>(false);
  private _user = signal<Auth0User | null>(null);
  private _accessToken = signal<string | null>(null);
  private _isLoading = signal<boolean>(false);

  readonly isAuthenticated = computed(() => this._isAuthenticated());
  readonly user = computed(() => this._user());
  readonly accessToken = computed(() => this._accessToken());
  readonly isLoading = computed(() => this._isLoading());

  private redirectCallbackHandled = false;
  private auth0Client: any = null;
  private organizationData: OrganizationData | null = null;
  private refreshInFlight: Promise<string> | null = null;
  private tokenSetAt: number | null = null;

  private readonly DEFAULT_SCOPE = 'openid email profile offline_access';
  private readonly LOGIN_ROUTES = ['/login', '/login-blink', '/auth/login', '/auth/login-blink'];
  // Backends can transiently reject a just-issued token right after login;
  // inside this window a 401/403 gets one refresh-and-replay instead of a bounce.
  private readonly HANDSHAKE_WINDOW_MS = 15000;

  async init(): Promise<void> {
    try {
      this._isLoading.set(true);

      this.organizationData = await firstValueFrom(this.organizationService.getOrganizationData());

      if (!this.organizationData) {
        throw new Error('Organization data is required for Auth0 initialization');
      }

      const config = this.getAuth0Config();

      const { createAuth0Client } = await import('@auth0/auth0-spa-js');

      this.auth0Client = await createAuth0Client(config);

      const hasCodeAndState = this.hasRedirectCallback();

      if (hasCodeAndState && !this.redirectCallbackHandled) {
        await this.handleRedirectCallback();
        this.redirectCallbackHandled = true;

        return;
      }

      await this.validateAuthentication();

    } catch (error) {
      console.error('Auth0 initialization failed:', error);
      this._isAuthenticated.set(false);
      this._user.set(null);
      this._accessToken.set(null);
      throw error;
    } finally {
      this._isLoading.set(false);
    }
  }

  private getAuth0Config(): Auth0ClientOptions {
    const isLoginBlink = window.location.pathname === '/login-blink';

    const domain = isLoginBlink
      ? this.organizationData!.altOrganizationDomain || this.organizationData!.organizationDomain
      : this.organizationData!.organizationDomain;

    const clientId = isLoginBlink
      ? this.organizationData!.altClientId || this.organizationData!.clientId
      : this.organizationData!.clientId;

    const organizationId = isLoginBlink
      ? this.organizationData!.altOrganizationId || this.organizationData!.organizationId
      : this.organizationData!.organizationId;

    const authParams: Auth0ClientOptions['authorizationParams'] = {
      redirect_uri: this.buildRedirectUri(),
      scope: this.DEFAULT_SCOPE,
    };

    if (organizationId) {
      authParams.organization = organizationId;
    }

    return {
      domain,
      clientId,
      authorizationParams: authParams,
      useRefreshTokens: true,
      cacheLocation: 'localstorage',
    };
  }

  private buildRedirectUri(): string {
    const path = window.location.pathname;

    // Auth0 only accepts listed callback addresses, and e-link pages are not listed
    // one by one. A sign-in that starts on a public page returns through /callback;
    // the page itself travels in appState.redirectTo.
    if (isPublicRoute(path)) {
      return `${window.location.origin}/callback`;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const blinkId = urlParams.get('blink_id');
    const state = blinkId ? `?blink_id=${blinkId}` : '';

    const isLoginRoute = path === '/' || path === '/login' || path === '/login-blink';
    const windowPath = isLoginRoute ? '/links' : path;

    return `${window.location.origin}${windowPath}${state}`;
  }

  private hasRedirectCallback(): boolean {
    return window.location.search.includes('code=') &&
           window.location.search.includes('state=');
  }

  private isLoginRoute(path: string = window.location.pathname): boolean {
    return this.LOGIN_ROUTES.includes(path);
  }

  private async handleRedirectCallback(): Promise<void> {
    if (!this.auth0Client) {
      throw new Error('Auth0 client not initialized');
    }

    try {
      const result = await this.auth0Client.handleRedirectCallback();

      // Clean up URL parameters to prevent re-processing
      this.cleanupUrlParams();

      // Get token and user info after successful callback
      await this.updateAuthState();

      // Navigate to intended destination. Deliberately not awaited: the target
      // route's guards wait for /me to load, and /me is only dispatched after
      // init() resolves — awaiting the navigation here would deadlock both.
      if (result?.appState?.redirectTo) {
        void this.router.navigateByUrl(result.appState.redirectTo, { replaceUrl: true });
      }
    } catch (error) {
      console.error('Auth0 redirect callback error:', error);
      this.cleanupUrlParams();
      this._isAuthenticated.set(false);
      this._user.set(null);
      this._accessToken.set(null);
      throw error;
    }
  }

  /**
   * Checks if user is authenticated by attempting to get a token
   * If token is expired, SDK will automatically refresh it using refresh token
   * Uses id_token to match React app behavior
   */
  private async validateAuthentication(): Promise<void> {
    if (!this.auth0Client) {
      return;
    }

    try {
      // Attempt to get token with detailed response to extract id_token
      // SDK will auto-refresh if expired
      const tokenResponse = await this.auth0Client.getTokenSilently({
        cacheMode: 'on',
        detailedResponse: true,
      }) as Auth0TokenResponse;

      const idToken = tokenResponse.id_token || tokenResponse.access_token || '';

      if (!idToken) {
        throw new Error('No token available');
      }

      const user = await this.auth0Client.getUser();

      this._isAuthenticated.set(true);
      this._user.set(user);
      this._accessToken.set(idToken);
      this.storeTokenForReactCompatibility(idToken);

    } catch (error: unknown) {
      console.warn('Token validation failed:', error);
      this._isAuthenticated.set(false);
      this._user.set(null);
      this._accessToken.set(null);

      const currentPath = window.location.pathname;

      // Never force login on login routes, public e-link routes, or mid-callback.
      // External (non-Blink) users land on public e-link pages with no session and
      // must be left there instead of being bounced into SSO by the global initializer.
      if (
        !this.isLoginRoute(currentPath) &&
        !isPublicRoute(currentPath) &&
        !this.hasRedirectCallback()
      ) {
        await this.loginWithRedirect(currentPath);
      }
    }
  }

  /**
   * Update Authentication State
   *
   * Fetches fresh token and user info and updates signals
   * Uses id_token to match React app behavior
   */
  private async updateAuthState(): Promise<void> {
    if (!this.auth0Client) {
      return;
    }

    try {
      // Get token with detailed response to extract id_token
      const tokenResponse = await this.auth0Client.getTokenSilently({
        cacheMode: 'on',
        detailedResponse: true,
      }) as Auth0TokenResponse;

      // Extract id_token (React app uses this)
      const idToken = tokenResponse.id_token || tokenResponse.access_token || '';

      const user = await this.auth0Client.getUser();

      this._isAuthenticated.set(true);
      this._user.set(user);
      this._accessToken.set(idToken);
      this.storeTokenForReactCompatibility(idToken);
    } catch (error) {
      console.warn('Failed to update auth state:', error);
      this._isAuthenticated.set(false);
      this._user.set(null);
      this._accessToken.set(null);
    }
  }

  /**
   * Clean up URL parameters (code, state) after OAuth callback
   */
  private cleanupUrlParams(): void {
    const url = new URL(window.location.href);

    url.searchParams.delete('code');
    url.searchParams.delete('state');
    window.history.replaceState({}, '', url.toString());
  }

  async loginWithRedirect(redirectTo?: string): Promise<void> {
    if (!this.auth0Client) { // FIXME: refactor when react-app gone
      await this.init();
    }

    const isLoginBlink = window.location.pathname === '/login-blink';
    const organizationId = isLoginBlink
      ? this.organizationData?.altOrganizationId || this.organizationData?.organizationId
      : this.organizationData?.organizationId;

    const authParams: any = {
      redirect_uri: this.buildRedirectUri(),
      scope: this.DEFAULT_SCOPE,
    };

    if (organizationId) {
      authParams.organization = organizationId;
    }

    await this.auth0Client.loginWithRedirect({
      authorizationParams: authParams,
      appState: redirectTo ? { redirectTo } : undefined,
    });
  }

  getAccessToken(): string | null {
    return this._accessToken();
  }

  async getAccessTokenSilently(options?: GetTokenSilentlyOptions): Promise<string | any> {
    if (!this.auth0Client) { // FIXME: refactor after react-app gone
      throw new Error('Auth0 client not initialized');
    }

    // Check if we need to force refresh (cacheMode: 'off' or ignoreCache from React app)
    const forceRefresh = options?.cacheMode === 'off' || (options as any)?.ignoreCache === true;
    const needsDetailedResponse = options?.detailedResponse === true;

    // If detailedResponse is requested or force refresh, always call SDK
    // Otherwise, use cached token if available
    const cachedToken = this._accessToken();

    if (cachedToken && !forceRefresh && !needsDetailedResponse) {
      return cachedToken;
    }

    // Handle scope - React app may pass it directly or in authorizationParams
    const requestedScope = options?.authorizationParams?.scope || (options as any)?.scope;
    let scope: string;

    if (!requestedScope) {
      scope = this.DEFAULT_SCOPE;
    } else if (requestedScope.includes('offline_access')) {
      scope = requestedScope;
    } else {
      scope = `${requestedScope} offline_access`;
    }

    const tokenOptions: any = {
      authorizationParams: {
        scope,
      },
      detailedResponse: needsDetailedResponse,
      cacheMode: forceRefresh ? 'off' : (options?.cacheMode || 'on'),
    };

    if (options?.authorizationParams?.audience) {
      tokenOptions.authorizationParams.audience = options.authorizationParams.audience;
    }

    if (options?.authorizationParams?.organization) {
      tokenOptions.authorizationParams.organization =
        options.authorizationParams.organization;
    }

    let token: string | Auth0TokenResponse;

    try {
      token = await this.auth0Client.getTokenSilently(tokenOptions) as string | Auth0TokenResponse;
    } catch (error) {
      console.error('Failed to get token from Auth0 SDK:', error);
      throw error;
    }

    if (needsDetailedResponse) {
      let accessToken: string;
      let idToken: string;

      if (typeof token === 'object' && token !== null) {
        const response = token as Auth0TokenResponse;

        idToken = response.id_token || response.access_token || '';
        accessToken = response.access_token || response.id_token || idToken;

        if (idToken) {
          this._accessToken.set(idToken);
          this.storeTokenForReactCompatibility(idToken);
        }
      } else if (typeof token === 'string' && token) {
        accessToken = token;
        idToken = token;
        this._accessToken.set(token);
        this.storeTokenForReactCompatibility(token);
      } else {
        console.warn('Token is null/undefined, attempting to get user and token separately');

        const simpleToken = await this.auth0Client.getTokenSilently({
          authorizationParams: { scope },
          detailedResponse: false,
          cacheMode: forceRefresh ? 'off' : 'on',
        });

        accessToken = simpleToken || '';
        idToken = simpleToken || '';

        if (idToken) {
          this._accessToken.set(idToken);
          this.storeTokenForReactCompatibility(idToken);
        }
      }

      return {
        access_token: accessToken,
        id_token: idToken,
        token_type: 'Bearer',
      };
    }

    if (!token) {
      throw new Error('No token received from Auth0 SDK');
    }

    if (typeof token === 'string') {
      this._accessToken.set(token);
      this.storeTokenForReactCompatibility(token);
    } else if (typeof token === 'object' && token !== null) {
      const response = token as Auth0TokenResponse;
      const idToken = response.id_token || response.access_token || '';

      if (idToken) {
        this._accessToken.set(idToken);
        this.storeTokenForReactCompatibility(idToken);

        return idToken;
      }
    }

    return token;
  }

  async getRefreshTokenForRegenerate(): Promise<string> {
    if (!this.auth0Client) {
      throw new Error('Auth0 client not initialized');
    }

    await this.auth0Client.getTokenSilently({
      authorizationParams: {
        scope: this.DEFAULT_SCOPE,
      },
      detailedResponse: true,
      cacheMode: 'off',
    });

    return this.readRefreshTokenFromCache();
  }

  private readRefreshTokenFromCache(): string {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);

      if (!key?.includes('auth0spa')) {
        continue;
      }

      try {
        const raw = localStorage.getItem(key);

        if (!raw) {
          continue;
        }

        const data = JSON.parse(raw) as { body?: { refresh_token?: string }; refresh_token?: string };
        const refreshToken = data?.body?.refresh_token ?? data?.refresh_token;

        if (refreshToken) {
          return refreshToken;
        }
      } catch {
        continue;
      }
    }

    return '';
  }

  isWithinAuthHandshakeWindow(): boolean {
    return this.tokenSetAt !== null && performance.now() - this.tokenSetAt < this.HANDSHAKE_WINDOW_MS;
  }

  async refreshAccessToken(): Promise<string> {
    // Single-flight: concurrent 401s during the handshake must share one
    // refresh — parallel cacheMode:'off' calls race refresh-token rotation.
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = this.doRefreshAccessToken().finally(() => {
      this.refreshInFlight = null;
    });

    return this.refreshInFlight;
  }

  private async doRefreshAccessToken(): Promise<string> {
    if (!this.auth0Client) {
      throw new Error('Auth0 client not initialized');
    }

    this._accessToken.set(null);

    const tokenResponse = await this.auth0Client.getTokenSilently({
      authorizationParams: {
        scope: this.DEFAULT_SCOPE,
      },
      detailedResponse: true,
      cacheMode: 'off',
    }) as Auth0TokenResponse;

    const idToken = tokenResponse.id_token || tokenResponse.access_token || '';

    if (idToken) {
      this._accessToken.set(idToken);
      this.storeTokenForReactCompatibility(idToken);
    }

    return idToken;
  }

  async logout(returnTo?: string): Promise<void> {
    if (!this.auth0Client) {
      return;
    }

    await this.auth0Client.logout({
      logoutParams: {
        returnTo: returnTo || window.location.origin,
      },
    });

    this._isAuthenticated.set(false);
    this._user.set(null);
    this._accessToken.set(null);
    localStorage.removeItem('auth0Token');
  }

  async getCurrentUser(): Promise<Auth0User | null> {
    if (!this.auth0Client) {
      return null;
    }

    const user = await this.auth0Client.getUser();

    if (user) {
      this._user.set(user);
    }

    return user;
  }

  async getAuthStateForReact(): Promise<{
    isAuthenticated: boolean;
    user: Auth0User | null;
    accessToken: string | null;
    isLoading: boolean;
  }> {
    const isAuthenticated = this._isAuthenticated();
    const user = this._user();
    const isLoading = this._isLoading();

    let accessToken: string | null = this._accessToken();

    if (isAuthenticated && !accessToken) {
      try {
        accessToken = await this.getAccessTokenSilently();
      } catch (error) {
        console.warn('Failed to get access token:', error);
      }
    }

    return {
      isAuthenticated,
      user,
      accessToken,
      isLoading,
    };
  }

  /**
   * Store Token for React Compatibility
   *
   * FIXME: Remove after React app migration
   * Temporary bridge for React app compatibility
   *
   * Stores token in localStorage for React app to use
   */
  private storeTokenForReactCompatibility(accessToken: string): void {
    // Called at every point a real token is set — doubles as the
    // handshake-window anchor for isWithinAuthHandshakeWindow().
    this.tokenSetAt = performance.now();
    localStorage.setItem('auth0Token', JSON.stringify({
      accessToken,
      tokenType: 'Bearer'
    }));
  }
}
